import { A } from '@adornis/base/env-info.js';
import { logger } from '@adornis/base/logging.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { ID } from '@adornis/baseql/baseqlTypes.js';
import { DataLoadedField } from '@adornis/baseql/decorators/data-loaded-field.js';
import { Arg, Entity, Field, Query, Subscription } from '@adornis/baseql/decorators.js';
import { MongoEntity } from '@adornis/baseql/entities/mongoEntity.js';
import { getCollectionHandle, getRawCollection } from '@adornis/baseql/server/collections.js';
import { publishFilter } from '@adornis/baseql/server/context.js';
import { loadMongoData } from '@adornis/baseql/server/data-loader.js';
import { watchQuery } from '@adornis/baseql/server/watchQuery.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { GLOBAL_CONTEXT } from '@adornis/users/db/a-roles.js';
import type { Weekday } from '@chancenwerk/enums/enums.js';
import { DateTime } from 'luxon';
import { catchError, from, of, switchMap } from 'rxjs';
import { CourseFilter } from '../filter/CourseFilter.js';
import { KPF } from '../master-data-management/kpf/entities/KPF.js';
import { ChancenwerkUser } from '../master-data-management/users/entities/ChancenwerkUser.js';
import { Student } from '../master-data-management/users/entities/Student.js';

// Constants
const COURSE_NOT_ARCHIVED_FILTER = { archivedAt: null };

// Utils
/**
 * Archives a course by setting the archivedAt field to the current date and time
 * @param course The course to archive
 */
export const archiveCourse = async (course: Course) => {
  if (!course) {
    throw new Error('Course not found');
  }
  course.archivedAt = DateTime.now();
  await course.save();
};

/**
 * @entity Course
 *
 * Since CW_02_26 we don't remove courses anymore, but rather only filter them out using the archivedAt field
 */
@Entity()
export class Course extends MongoEntity {
  public static override _class = 'Course';
  public static override _collectionName = 'courses';

  // todo: currently duplicated from courseHelper, remove redundancy
  private readonly _COURSE_TITLES = { CourseLernfoerderung: 'Lernförderung', CourseIntensive: 'Intensivkurs' };

  @Field(() => String, { default: v => v ?? '' })
  public name!: string;

  @Field(() => String, { default: v => v ?? GLOBAL_CONTEXT })
  public kpfID!: string;

  @Field(() => KPF, {
    resolve: function (this: Course) {
      return async () =>
        this.kpfID !== '' && this.kpfID !== GLOBAL_CONTEXT
          ? (loadMongoData(KPF, this.kpfID) as Promise<Maybe<KPF>>)
          : null;
    },
  })
  public kpf!: KPF;

  // Defaults to empty string because we map courses according to this field and maps cannot work with undefined
  @Field(() => String, { default: v => v ?? '' })
  public location!: string;

  @Field(() => String)
  public weekday: Maybe<Weekday>;

  @Field(() => DateTime)
  public startTime: Maybe<DateTime>;

  @Field(() => DateTime)
  public endTime: Maybe<DateTime>;

  @Field(() => DateTime, { default: v => v ?? DateTime.now() })
  public createdAt!: DateTime;

  @Field(() => DateTime)
  public plannedStartDate: Maybe<DateTime>;

  @Field(() => DateTime)
  public startDate: Maybe<DateTime>;

  @Field(() => [String], { default: v => v ?? [] })
  public schuKoIDs: Maybe<string[]>;

  @DataLoadedField(() => [ChancenwerkUser], ({ schuKoIDs }) => schuKoIDs ?? [])
  public schuKos: Maybe<ChancenwerkUser[]>;

  @Field(() => [String], { default: v => v ?? [] })
  public leKoIDs: Maybe<string[]>;

  @DataLoadedField(() => [ChancenwerkUser], ({ leKoIDs }) => leKoIDs ?? [])
  public leKos: Maybe<ChancenwerkUser[]>;

  @Field(() => [String], { default: v => v ?? [] })
  public osStudentIDs!: string[];

  @DataLoadedField(() => [Student], ({ osStudentIDs }) => osStudentIDs)
  public osStudents!: Student[];

  @Field(() => [String], { default: v => v ?? [] })
  public usStudentIDs!: string[];

  @DataLoadedField(() => [Student], ({ usStudentIDs }) => usStudentIDs)
  public usStudents!: Student[];

  @Field(() => DateTime)
  public archivedAt?: Maybe<DateTime>;

  public get stringRepresentation(): string {
    return `${this._COURSE_TITLES[this._class] as string} ${this.weekday ?? ''} ${
      this.location
    } ${this.startTime?.toFormat('HH:mm')}`;
  }

  /**
   * This function provides help and deduplication for assigning users to courses.
   * @param userToAssign The user which should be assigned
   * @param courseToAssignTo The new course for the user
   * @param options options object, with the array where the user should be assigned to ('leKo' | 'OS' | 'US') default = US, and the old course where the user should be unassigned
   */
  public static assignUserToCourse(
    userToAssign: ChancenwerkUser,
    courseToAssignTo: Course | null,
    options: { arrayToAssign: 'SchuKo' | 'LeKo' | 'OS' | 'US'; oldCourses?: Course[] } = { arrayToAssign: 'US' },
  ) {
    if (!userToAssign) throw new Error('Cannot assign falsy user!');

    // Unassign from old course if there is an array with that user.
    // A user occupies at most one role-array per course, but we clear every array
    // defensively: guarding each splice on its own `!== -1` check so that
    //   - a user at index 0 is still removed (the old truthiness guard skipped them), and
    //   - an unpopulated array earlier in the list can no longer stop later arrays from being checked.
    if (options.oldCourses?.length) {
      for (const oldCourse of options.oldCourses) {
        const roleArrays: Array<[Maybe<string[]>, Maybe<Array<{ _id: string }>>]> = [
          [oldCourse.leKoIDs, oldCourse.leKos],
          [oldCourse.schuKoIDs, oldCourse.schuKos],
          [oldCourse.osStudentIDs, oldCourse.osStudents],
          [oldCourse.usStudentIDs, oldCourse.usStudents],
        ];
        for (const [ids, users] of roleArrays) {
          const indexIDs = ids?.indexOf(userToAssign._id) ?? -1;
          if (indexIDs !== -1) ids?.splice(indexIDs, 1);

          const indexUsers = users?.findIndex(u => u._id === userToAssign._id) ?? -1;
          if (indexUsers !== -1) users?.splice(indexUsers, 1);
        }
      }
    }

    // Assign to new Array
    if (courseToAssignTo) {
      let indexSchuKo: number | undefined, indexLeKo: number | undefined, indexOS: number, indexUS: number;

      switch (options.arrayToAssign) {
        case 'SchuKo':
          indexSchuKo = courseToAssignTo.schuKoIDs?.indexOf(userToAssign._id);
          if (indexSchuKo !== -1) {
            logger.warn(
              'User with _id:',
              userToAssign._id,
              'is already in course with _id:',
              courseToAssignTo._id,
              'Skipping to prevent duplicate!',
            );
            break;
          }
          courseToAssignTo.schuKoIDs?.push(userToAssign._id);
          courseToAssignTo.schuKos?.push(userToAssign);
          break;
        case 'LeKo':
          indexLeKo = courseToAssignTo.leKoIDs?.indexOf(userToAssign._id);
          if (indexLeKo !== -1) {
            logger.warn(
              'User with _id:',
              userToAssign._id,
              'is already in course with _id:',
              courseToAssignTo._id,
              'Skipping to prevent duplicate!',
            );
            break;
          }
          courseToAssignTo.leKoIDs?.push(userToAssign._id);
          courseToAssignTo.leKos?.push(userToAssign);
          break;
        case 'OS':
          indexOS = courseToAssignTo.osStudentIDs.indexOf(userToAssign._id);
          if (indexOS !== -1) {
            logger.warn(
              'User with _id:',
              userToAssign._id,
              'is already in course with _id:',
              courseToAssignTo._id,
              'Skipping to prevent duplicate!',
            );
            break;
          }
          courseToAssignTo.osStudentIDs.push(userToAssign._id);
          courseToAssignTo.osStudents.push(userToAssign as Student);
          break;
        case 'US':
          indexUS = courseToAssignTo.usStudentIDs.indexOf(userToAssign._id);
          if (indexUS !== -1) {
            logger.warn(
              'User with _id:',
              userToAssign._id,
              'is already in course with _id:',
              courseToAssignTo._id,
              'Skipping to prevent duplicate!',
            );
            break;
          }
          courseToAssignTo.usStudentIDs.push(userToAssign._id);
          courseToAssignTo.usStudents.push(userToAssign as Student);
          break;
        default:
          break;
      }
    }
  }

  @Subscription(() => [Course])
  public static subscribeCoursesByKPFID(
    @Arg('kpfID', () => [String]) kpfIDs: string[],
    @Arg('courseType', () => String) courseType: string = 'CourseLernfoerderung',
  ) {
    return (gqlFields: BaseQLSelectionSet<Course>) => {
      const selector = { $or: [{ kpfID: { $in: kpfIDs } }, { openToAll: true }], _class: courseType };
      const handle = getCollectionHandle<Course>(this._collectionName);
      return from(publishFilter(this._class)).pipe(
        switchMap(publishFilter => {
          return handle.watchQuery(publishFilter ? { $and: [publishFilter, selector] } : selector);
        }),
        catchError(e => {
          logger.error(e);
          return of([] as Course[]);
        }),
      );
    };
  }

  @Subscription(() => [Course])
  public static subscribeAllCoursesByKPFID(@Arg('kpfID', () => String) kpfID: string) {
    return (gqlFields: BaseQLSelectionSet<Course>) => {
      const selector = { $or: [{ kpfID }, { openToAll: true }] };
      const handle = getCollectionHandle<Course>(this._collectionName);
      return from(publishFilter(this._class)).pipe(
        switchMap(publishFilter => {
          return handle.watchQuery(publishFilter ? { $and: [publishFilter, selector] } : selector);
        }),
        catchError(e => {
          logger.error(e);
          return of([] as Course[]);
        }),
      );
    };
  }

  @Subscription(() => [Course])
  public static subscribeAllCoursesIncludingArchivedByKPFID(@Arg('kpfID', () => String) kpfID: string) {
    return (gqlFields: BaseQLSelectionSet<Course>) => {
      const selector = { $or: [{ kpfID }, { openToAll: true }] };

      return watchQuery<Course>(this._collectionName, selector).pipe(
        catchError(e => {
          logger.error(e);
          return of([] as Course[]);
        }),
      );
    };
  }

  @Subscription(() => [Course])
  public static subscribeCoursesByFilter(@Arg('filter', () => CourseFilter) filter: CourseFilter) {
    return (gqlFields: BaseQLSelectionSet<Course>) => {
      const selector = filter.getMongoFilter();
      const handle = getCollectionHandle<Course>(this._collectionName);
      return from(publishFilter(this._class)).pipe(
        switchMap(publishFilter => {
          return handle.watchQuery(publishFilter ? { $and: [publishFilter, selector] } : selector);
        }),
        catchError(e => {
          logger.error(e);
          return of([] as Course[]);
        }),
      );
    };
  }

  @Subscription(() => [Course])
  public static subscribeCoursesByFilterForUser(
    @Arg('userId', () => ID) userId: string,
    @Arg('filter', () => CourseFilter) filter: CourseFilter,
  ) {
    return (gqlFields: BaseQLSelectionSet<Course>) => {
      const selector = {
        $and: [filter.getMongoFilter(), { $or: [{ osStudentIDs: userId }, { usStudentIDs: userId }] }],
      };
      const handle = getCollectionHandle<Course>(this._collectionName);
      return from(publishFilter(this._class)).pipe(
        switchMap(publishFilter => {
          return handle.watchQuery(publishFilter ? { $and: [publishFilter, selector] } : selector);
        }),
        catchError(e => {
          logger.error(e);
          return of([] as Course[]);
        }),
      );
    };
  }

  @Query(() => [Course])
  static getCourseForUser(
    @Arg('userId', () => ID) userId: string,
    @Arg('filter', () => CourseFilter) filter: CourseFilter,
  ) {
    return async (gqlFields: BaseQLSelectionSet<Course>) => {
      const selector = filter.getMongoFilter();
      const collection = await getRawCollection<Course>(this._collectionName);
      const res = await collection
        .find({
          $and: [selector, { $or: [{ osStudentIDs: userId }, { usStudentIDs: userId }] }, COURSE_NOT_ARCHIVED_FILTER],
        })
        .toArray();

      if (!res) {
        return [];
      }

      return res as Course[];
    };
  }
}

if (A.isServer) {
  const col = getRawCollection<any>(Course._collectionName);

  // Create DB Indices
  void col.then(async db => {
    await db.createIndex({ osStudentIDs: 1 });
    await db.createIndex({ usStudentIDs: 1 });
    await db.createIndex({ leKoIDs: 1 });
    await db.createIndex({ schuKoIDs: 1 });
    await db.createIndex({ kpfID: 1 });
  });
}
